import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEventsV3TicketDefinition from './events-v3-ticket-definition-ticket-definitions-v-2.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformSDKPageURLToRESTPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A Ticket Definition is a reusable configuration for an event that specifies ticket settings such as pricing, service fee handling, availability limits, and sale period.
 *
 * You can use Ticket Definitions to define and manage ticket types, control inventory and sale windows, and automate updates via the API.
 *
 * Read more about [Ticket Definitions](https://support.wix.com/en/article/creating-tickets-for-your-event).
 */
export interface TicketDefinition {
  /**
   * Ticket definition ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Event ID to which the ticket definition belongs.
   * @format GUID
   * @immutable
   */
  eventId?: string | null;
  /**
   * Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ticket definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the ticket definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Ticket definition name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string | null;
  /**
   * Ticket definition description.
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Ticket definition policy.
   * @maxLength 1000
   */
  policyText?: string | null;
  /** Whether this ticket definition is hidden from site visitors and can't be purchased. */
  hidden?: boolean;
  /**
   * Whether the ticket has a limited maximum quantity.
   * @readonly
   */
  limited?: boolean;
  /** The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request. */
  initialLimit?: number | null;
  /**
   * The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.
   * @readonly
   */
  actualLimit?: number | null;
  /** Ticket pricing method. */
  pricingMethod?: PricingMethod;
  /** Type of ticket service fee to collect. */
  feeType?: FeeTypeEnumTypeWithLiterals;
  /** Ticket sale period. */
  salePeriod?: SalePeriod;
  /**
   * Ticket sale status.
   * @readonly
   */
  saleStatus?: SaleStatusEnumStatusWithLiterals;
  /**
   * Ticket sales information. <br> <br>
   * **Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.
   * @readonly
   */
  salesDetails?: SalesDetails;
  /**
   * Number of tickets that can be purchased per checkout. <br> <br>
   * **Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.
   * @readonly
   * @max 50
   */
  limitPerCheckout?: number | null;
  /** Data extensions. */
  extendedFields?: ExtendedFields;
  /**
   * Information about the even the ticket is for. <br>
   * **Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.
   * @readonly
   */
  eventDetails?: EventDetails;
  /**
   * Seating information including available seats and areas for this ticket definition. <br>
   * **Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).
   * @readonly
   */
  seatingDetails?: SeatingDetails;
}

export interface SalePeriod {
  /** Date and time the ticket sale starts. Rounded down to the nearest minute. */
  startDate?: Date | null;
  /** Date and time the ticket sale ends. Rounded down to the nearest minute. */
  endDate?: Date | null;
  /** Whether to display the ticket if it's not available to buy. */
  displayNotOnSale?: boolean;
}

export interface PricingMethod extends PricingMethodPriceOneOf {
  /** Same ticket price for everyone. */
  fixedPrice?: CommonMoney;
  /** Guests choose how much they'd like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout. */
  guestPrice?: CommonMoney;
  /** Sets of various ticket prices. For example, you can charge different prices for children and adults. */
  pricingOptions?: PricingOptions;
  /**
   * Ticket price type.
   * @readonly
   */
  pricingType?: PricingTypeEnumTypeWithLiterals;
  /**
   * Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.
   * @readonly
   */
  free?: boolean;
}

/** @oneof */
export interface PricingMethodPriceOneOf {
  /** Same ticket price for everyone. */
  fixedPrice?: CommonMoney;
  /** Guests choose how much they'd like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout. */
  guestPrice?: CommonMoney;
  /** Sets of various ticket prices. For example, you can charge different prices for children and adults. */
  pricingOptions?: PricingOptions;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface CommonMoney {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
}

export interface PricingOptions {
  /**
   * Ticket price options.
   * @maxSize 100
   */
  optionDetails?: OptionDetails[];
}

export interface OptionDetails {
  /**
   * Ticket price option ID.
   * @format GUID
   */
  optionId?: string | null;
  /**
   * Ticket price option name, such as "Child Ticket".
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Ticket price. */
  price?: CommonMoney;
}

export enum PricingTypeEnumType {
  /** All money goes to a seller. Applies to all ticket pricing methods except for `guestPrice`. */
  STANDARD = 'STANDARD',
  /** All collected money is a donation. This pricing type is automatically assigned when you select the `guestPrice` pricing method. */
  DONATION = 'DONATION',
}

/** @enumType */
export type PricingTypeEnumTypeWithLiterals =
  | PricingTypeEnumType
  | 'STANDARD'
  | 'DONATION';

export enum FeeTypeEnumType {
  /** The fee is deducted from the ticket price for a seller. <br><br> For example, if you're selling tickets for $10, then a ticket service fee of $0.25 will be deducted from the price and you'll get $9.75. */
  FEE_INCLUDED = 'FEE_INCLUDED',
  /** The fee is shown in addition to the ticket price at checkout and a guest pays the fee. <br><br> For example, if you sell tickets for $10, a customer will see a ticket service fee of $0.25 and will pay $10.25 in total. */
  FEE_ADDED_AT_CHECKOUT = 'FEE_ADDED_AT_CHECKOUT',
  /** Ticket service fee isn't collected. Available only for free tickets and legacy Wix users. */
  NO_FEE = 'NO_FEE',
}

/** @enumType */
export type FeeTypeEnumTypeWithLiterals =
  | FeeTypeEnumType
  | 'FEE_INCLUDED'
  | 'FEE_ADDED_AT_CHECKOUT'
  | 'NO_FEE';

export enum SaleStatusEnumStatus {
  /** Tickets aren't on sale yet. */
  SALE_SCHEDULED = 'SALE_SCHEDULED',
  /** Tickets are on sale. */
  SALE_STARTED = 'SALE_STARTED',
  /** Tickets are no longer on sale. */
  SALE_ENDED = 'SALE_ENDED',
}

/** @enumType */
export type SaleStatusEnumStatusWithLiterals =
  | SaleStatusEnumStatus
  | 'SALE_SCHEDULED'
  | 'SALE_STARTED'
  | 'SALE_ENDED';

export interface SalesDetails {
  /**
   * Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited.
   * @readonly
   */
  unsoldCount?: number | null;
  /**
   * Number of purchased tickets.
   * @readonly
   */
  soldCount?: number | null;
  /**
   * Number of reserved tickets.
   * @readonly
   */
  reservedCount?: number | null;
  /**
   * Whether the tickets are sold out.
   * @readonly
   */
  soldOut?: boolean | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface EventDetails {
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   * @readonly
   */
  title?: string | null;
  /**
   * Short description of the event.
   * @maxLength 500
   * @readonly
   */
  shortDescription?: string | null;
  /**
   * Event location.
   * @readonly
   */
  location?: Location;
  /**
   * Event date and time settings.
   * @readonly
   */
  dateAndTimeSettings?: DateAndTimeSettings;
  /**
   * Event page URL components.
   * @readonly
   */
  eventPageUrl?: string;
  /**
   * Event status.
   * @readonly
   */
  status?: StatusWithLiterals;
}

export interface Location {
  /**
   * Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.
   * @maxLength 50
   */
  name?: string | null;
  /** Location type. */
  type?: LocationTypeWithLiterals;
  /** Exact location address. */
  address?: CommonAddress;
  /** Whether the event location is TBD. */
  locationTbd?: boolean | null;
}

export enum LocationType {
  /** Event is on-site at a specific physical location. */
  VENUE = 'VENUE',
  /** Event is online, such as a virtual video conference. */
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationTypeWithLiterals = LocationType | 'VENUE' | 'ONLINE';

/** Physical address */
export interface CommonAddress extends CommonAddressStreetOneOf {
  /** Street address. */
  streetAddress?: CommonStreetAddress;
  /**
   * Primary address information (street and building number).
   * @maxLength 250
   */
  addressLine1?: string | null;
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).
   * @maxLength 20
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 100
   */
  city?: string | null;
  /**
   * Zip or postal code.
   * @maxLength 100
   */
  postalCode?: string | null;
  /**
   * Secondary address information (suite or apartment number and room number).
   * @maxLength 250
   */
  addressLine2?: string | null;
}

/** @oneof */
export interface CommonAddressStreetOneOf {
  /** Street address. */
  streetAddress?: CommonStreetAddress;
  /**
   * Primary address information (street and building number).
   * @maxLength 250
   */
  addressLine?: string | null;
}

export interface CommonStreetAddress {
  /**
   * Street number.
   * @maxLength 100
   */
  number?: string;
  /**
   * Street name.
   * @maxLength 250
   */
  name?: string;
}

export interface CommonAddressLocation {
  /**
   * Address latitude coordinates.
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * Address longitude coordinates.
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface CommonSubdivision {
  /**
   * Short subdivision code.
   * @maxLength 100
   */
  code?: string;
  /**
   * Subdivision full name.
   * @maxLength 250
   */
  name?: string;
}

export enum SubdivisionSubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionSubdivisionTypeWithLiterals =
  | SubdivisionSubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface DateAndTimeSettings {
  /** Whether the event date and time are TBD. */
  dateAndTimeTbd?: boolean | null;
  /**
   * Message that is displayed when time and date is TBD.
   *
   * **Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.
   * @maxLength 100
   */
  dateAndTimeTbdMessage?: string | null;
  /**
   * Event start date.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   */
  startDate?: Date | null;
  /**
   * Event end date.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   */
  endDate?: Date | null;
  /**
   * Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether the end date is hidden in the formatted date and time. */
  hideEndDate?: boolean | null;
  /** Whether the time zone is displayed in the formatted schedule. */
  showTimeZone?: boolean | null;
  /**
   * Repeating event status.
   * @readonly
   */
  recurrenceStatus?: RecurrenceStatusStatusWithLiterals;
  /** Event repetitions. */
  recurringEvents?: Recurrences;
  /** Formatted date and time settings. */
  formatted?: Formatted;
}

export enum RecurrenceStatusStatus {
  /** Event happens only once and can last multiple days. */
  ONE_TIME = 'ONE_TIME',
  /** A series of events that repeat. */
  RECURRING = 'RECURRING',
  /** Next event in a schedule of recurring events. */
  RECURRING_UPCOMING = 'RECURRING_UPCOMING',
  /** Latest event that ended in a schedule of recurring events. */
  RECURRING_RECENTLY_ENDED = 'RECURRING_RECENTLY_ENDED',
  /** Latest canceled event in a schedule of recurring events */
  RECURRING_RECENTLY_CANCELED = 'RECURRING_RECENTLY_CANCELED',
}

/** @enumType */
export type RecurrenceStatusStatusWithLiterals =
  | RecurrenceStatusStatus
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_UPCOMING'
  | 'RECURRING_RECENTLY_ENDED'
  | 'RECURRING_RECENTLY_CANCELED';

export interface Recurrences {
  /**
   * Individual event dates for recurring events.
   *
   * *Note:** Each date must be manually calculated and provided. There is no support for automatic generation using recurrence rules or patterns (for example, "Weekly").
   *
   * When you create a recurring event:
   * - Each occurrence is created as an independent event with its own unique ID.
   * - All occurrences in the series share the same `categoryId`, which allows you to identify all events in the recurring series.
   * - To retrieve all events in a recurring series, query events by `recurringEvents.categoryId`.
   * - Each event in the series can be independently updated or deleted using its individual event ID.
   * @maxSize 1000
   */
  individualEventDates?: Occurrence[];
  /**
   * Recurring event category ID.
   *
   * This read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.
   * @readonly
   * @maxLength 100
   */
  categoryId?: string | null;
}

export interface Occurrence {
  /** Event start date. */
  startDate?: Date | null;
  /** Event end date. */
  endDate?: Date | null;
  /**
   * Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether the time zone is displayed in a formatted schedule. */
  showTimeZone?: boolean;
}

export interface Formatted {
  /**
   * Formatted date and time representation. <br>
   * Example of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>
   * Example of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.
   * @readonly
   * @maxLength 500
   */
  dateAndTime?: string | null;
  /**
   * Formatted start date of the event. Empty for TBD schedules.
   * @readonly
   * @maxLength 500
   */
  startDate?: string | null;
  /**
   * Formatted start time of the event. Empty for TBD schedules.
   * @readonly
   * @maxLength 500
   */
  startTime?: string | null;
  /**
   * Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.
   * @readonly
   * @maxLength 500
   */
  endDate?: string | null;
  /**
   * Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.
   * @readonly
   * @maxLength 500
   */
  endTime?: string | null;
}

export enum Status {
  /** Event is published and scheduled to start. */
  UPCOMING = 'UPCOMING',
  /** Event has started. */
  STARTED = 'STARTED',
  /** Event has ended. */
  ENDED = 'ENDED',
  /** Event is canceled. */
  CANCELED = 'CANCELED',
  /** Event is not public. */
  DRAFT = 'DRAFT',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UPCOMING'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED'
  | 'DRAFT';

export interface SeatingDetails {
  /**
   * List of available places.
   * @maxSize 50000
   */
  places?: AvailablePlace[];
}

export interface AvailablePlace {
  /**
   * Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.
   * @minLength 1
   * @maxLength 20
   * @readonly
   */
  placeId?: string;
  /**
   * Human-readable label for this place, such as `A1`, `12`, or `VIP1`.
   * @minLength 1
   * @maxLength 4
   * @readonly
   */
  label?: string;
  /**
   * High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.
   * @minLength 1
   * @maxLength 20
   * @readonly
   */
  sectionLabel?: string | null;
  /**
   * Seating element within the section, such as `Row`, `Table`, or `General Admission`.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  elementLabel?: string | null;
  /**
   * Available capacity at this place.
   *
   * For general seating type returns the number of remaining spots.
   *
   * For other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.
   * @max 50000
   * @readonly
   */
  availableCapacity?: number;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface SalePeriodUpdated {
  /** Ticket definition sale period after update. */
  afterUpdate?: SalePeriod;
}

export interface TicketDefinitionSaleStarted {
  /** Ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface TicketDefinitionSaleEnded {
  /** Ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface CreateTicketDefinitionRequest {
  /** Ticket definition info. */
  ticketDefinition: TicketDefinition;
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export enum Field {
  /** Returns `salesDetails` in the response. */
  SALES_DETAILS = 'SALES_DETAILS',
  /** Returns `eventDetails` in the response. */
  EVENT_DETAILS = 'EVENT_DETAILS',
  /** Returns `seatingDetails` with available seats in the response. */
  SEATING_DETAILS = 'SEATING_DETAILS',
}

/** @enumType */
export type FieldWithLiterals =
  | Field
  | 'SALES_DETAILS'
  | 'EVENT_DETAILS'
  | 'SEATING_DETAILS';

export interface CreateTicketDefinitionResponse {
  /** Created ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface UpdateTicketDefinitionRequest {
  /** Ticket definition to update. */
  ticketDefinition: TicketDefinition;
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface UpdateTicketDefinitionResponse {
  /** The updated ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface GetTicketDefinitionRequest {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId: string;
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface GetTicketDefinitionResponse {
  /** The requested ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface DeleteTicketDefinitionRequest {
  /**
   * ID of the ticket definition to delete.
   * @format GUID
   */
  ticketDefinitionId: string;
}

export interface DeleteTicketDefinitionResponse {}

export interface ReorderTicketDefinitionsRequest
  extends ReorderTicketDefinitionsRequestReferenceDefinitionOneOf {
  /**
   * Move the given `definitionId` before the referenced ticket definition.
   * @format GUID
   */
  beforeDefinitionId?: string;
  /**
   * Move the given `definitionId` after the referenced ticket definition.
   * @format GUID
   */
  afterDefinitionId?: string;
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId: string;
}

/** @oneof */
export interface ReorderTicketDefinitionsRequestReferenceDefinitionOneOf {
  /**
   * Move the given `definitionId` before the referenced ticket definition.
   * @format GUID
   */
  beforeDefinitionId?: string;
  /**
   * Move the given `definitionId` after the referenced ticket definition.
   * @format GUID
   */
  afterDefinitionId?: string;
}

export interface ReorderTicketDefinitionsResponse {}

export interface UpdateTicketDefinitionSortIndexRequest {
  /**
   * Ticket definition ID
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** The revision of the ticket definition */
  revision?: string;
  /** the sort index of a ticket definition to set */
  sortIndex?: number;
  /**
   * Requested fields.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface UpdateTicketDefinitionSortIndexResponse {
  /** the updated ticket definition */
  ticketDefinition?: TicketDefinition;
}

export interface QueryTicketDefinitionsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query: QueryV2;
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort` parameters. */
  cursorPaging?: CursorPaging;
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1", "fieldName2":{"$operator":"value2"} }`. <br/> <br/> **Example:** <br/> `"filter" : { "id": "2224a9d1-79e6-4549-a5c5-bf7ce5aac1a5", "revision": {"$ne":"1"} }` */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format: <br/> `[{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]` <br/> <br/> **Example:** <br/> `[{"fieldName":"createdDate","direction":"DESC"}]` <br/> <br/> See [supported fields](https://dev.wix.com/api/rest/wix-events/policy-v2/filter-and-sort) for more information.
   * @maxSize 10
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort` parameters. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 100
   */
  fieldName?: string;
  /** Defaults to `ASC` */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load per page. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Number of items to load per page.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryTicketDefinitionsResponse {
  /** List of ticket definitions. */
  ticketDefinitions?: TicketDefinition[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 36
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 36
   */
  prev?: string | null;
}

export interface QueryAvailableTicketDefinitionsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query: QueryV2;
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface QueryAvailableTicketDefinitionsResponse {
  /** List of ticket definitions. */
  ticketDefinitions?: TicketDefinition[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadataV2;
}

export interface CountTicketDefinitionsRequest {
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`. */
  filter?: Record<string, any> | null;
  /**
   * Parameters to count ticket definitions by.
   *
   * - Max: 20 facets.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
}

export interface CountTicketDefinitionsResponse {
  /** Metadata for the paginated results. */
  metadata?: PagingMetadataV2;
  /** Filter facets. */
  facets?: Record<string, FacetCounts>;
}

export interface FacetCounts {
  /** Facet counts aggregated per value */
  counts?: Record<string, number>;
}

export interface CountAvailableTicketDefinitionsRequest {
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`. */
  filter?: Record<string, any> | null;
}

export interface CountAvailableTicketDefinitionsResponse {
  /** Metadata for the paginated results. */
  metadata?: PagingMetadataV2;
}

export interface BulkDeleteTicketDefinitionsByFilterRequest {
  /** Filter object. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  filter: Record<string, any> | null;
}

export interface BulkDeleteTicketDefinitionsByFilterResponse {}

export interface ChangeCurrencyRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * Ticket price currency in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @minLength 3
   * @maxLength 3
   */
  currency: string;
}

export interface ChangeCurrencyResponse {}

export interface BulkCopyTicketDefinitionsByEventIdRequest {
  /**
   * Origin instance ID.
   * @format GUID
   */
  originInstanceId?: string | null;
  /**
   * Origin Event ID.
   * @format GUID
   */
  originEventId?: string;
  /**
   * Target Event ID.
   * @format GUID
   */
  targetEventId?: string;
}

export interface BulkCopyTicketDefinitionsByEventIdResponse {
  /** Copied ticket definitions. */
  definitions?: CopiedTicketDefinition[];
}

export interface CopiedTicketDefinition {
  /**
   * Origin Ticket definition ID.
   * @format GUID
   */
  originTicketDefinitionId?: string;
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
}

export interface UpdateFeeTypesBasedOnSettingsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query?: QueryV2;
}

export interface UpdateFeeTypesBasedOnSettingsResponse {}

export interface EventDeleted {
  /** Event deleted timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
}

export interface Empty {}

export interface EventCanceled {
  /** Event canceled timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event title */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
  /** True if at least one guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface EventEnded {
  /** Event end timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** True if at least one guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface EventCreated {
  /** Event created timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: EventsLocation;
  /** Event schedule configuration. */
  scheduleConfig?: ScheduleConfig;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @maxLength 36
   */
  userId?: string | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /**
   * Instance ID. Indicates the original app instance which current event was derived from.
   * @format GUID
   */
  derivedFromInstanceId?: string | null;
  /**
   * Event ID. Indicates the original event which current event was derived from.
   * @format GUID
   */
  derivedFromEventId?: string | null;
  /** Event that was created. */
  event?: Event;
}

export interface EventsLocation {
  /**
   * Location name.
   * @maxLength 50
   */
  name?: string | null;
  /** Location map coordinates. */
  coordinates?: MapCoordinates;
  /**
   * Single line address representation.
   * @maxLength 300
   */
  address?: string | null;
  /** Location type. */
  type?: LocationLocationTypeWithLiterals;
  /**
   * Full address derived from formatted single line `address`.
   * When `full_address` is used to create or update the event, deprecated `address` and `coordinates` are ignored.
   * If provided `full_address` has empty `formatted_address` or `coordinates`, it will be auto-completed using Atlas service.
   *
   * Migration notes:
   * - `full_address.formatted_address` is equivalent to `address`.
   * - `full_address.geocode` is equivalent to `coordinates`.
   */
  fullAddress?: Address;
  /**
   * Defines event location as TBD (To Be Determined).
   * When event location is not yet defined, `name` is displayed instead of location address.
   * `coordinates`, `address`, `type` and `full_address` are not required when location is TBD.
   */
  tbd?: boolean | null;
}

export interface MapCoordinates {
  /**
   * Latitude.
   * @min -90
   * @max 90
   */
  lat?: number;
  /**
   * Longitude.
   * @min -180
   * @max 180
   */
  lng?: number;
}

export enum LocationLocationType {
  VENUE = 'VENUE',
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationLocationTypeWithLiterals =
  | LocationLocationType
  | 'VENUE'
  | 'ONLINE';

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine1?: string | null;
  /**
   * country code
   * @format COUNTRY
   */
  country?: string | null;
  /** subdivision (usually state or region) code according to ISO 3166-2 */
  subdivision?: string | null;
  /** city name */
  city?: string | null;
  /** zip/postal code */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, Floor */
  addressLine2?: string | null;
  /** A string containing the human-readable address of this location */
  formatted?: string | null;
  /** Free text for human-to-human textual orientation aid purposes */
  hint?: string | null;
  /** coordinates of the physical address */
  location?: AddressLocation;
  /** country full-name */
  countryFullname?: string | null;
  /**
   * multi-level subdivisions from top to bottom
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** street number */
  number?: string;
  /** street name */
  name?: string;
}

export interface AddressLocation {
  /**
   * address latitude coordinates
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * address longitude coordinates
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface Subdivision {
  /** subdivision short code */
  code?: string;
  /** subdivision full-name */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface ScheduleConfig {
  /**
   * Defines event as TBD (To Be Determined) schedule.
   * When event time is not yet defined, TBD message is displayed instead of event start and end times.
   * `startDate`, `endDate` and `timeZoneId` are not required when schedule is TBD.
   */
  scheduleTbd?: boolean;
  /**
   * TBD message.
   * @maxLength 100
   */
  scheduleTbdMessage?: string | null;
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether end date is hidden in the formatted schedule. */
  endDateHidden?: boolean;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
  /** Event recurrences. */
  recurrences?: EventsRecurrences;
}

export interface EventsRecurrences {
  /**
   * Event occurrences.
   * @maxSize 1000
   */
  occurrences?: EventsOccurrence[];
  /**
   * Recurring event category ID.
   * @readonly
   */
  categoryId?: string | null;
  /**
   * Recurrence status.
   * @readonly
   */
  status?: EventsRecurrenceStatusStatusWithLiterals;
}

export interface EventsOccurrence {
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
}

export enum EventsRecurrenceStatusStatus {
  /** Event occurs only once. */
  ONE_TIME = 'ONE_TIME',
  /** Event is recurring. */
  RECURRING = 'RECURRING',
  /** Marks the next upcoming occurrence of the recurring event. */
  RECURRING_NEXT = 'RECURRING_NEXT',
  /** Marks the most recent ended occurrence of the recurring event. */
  RECURRING_LAST_ENDED = 'RECURRING_LAST_ENDED',
  /** Marks the most recent canceled occurrence of the recurring event. */
  RECURRING_LAST_CANCELED = 'RECURRING_LAST_CANCELED',
}

/** @enumType */
export type EventsRecurrenceStatusStatusWithLiterals =
  | EventsRecurrenceStatusStatus
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_NEXT'
  | 'RECURRING_LAST_ENDED'
  | 'RECURRING_LAST_CANCELED';

export enum EventStatus {
  /** Event is public and scheduled to start */
  SCHEDULED = 'SCHEDULED',
  /** Event has started */
  STARTED = 'STARTED',
  /** Event has ended */
  ENDED = 'ENDED',
  /** Event was canceled */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type EventStatusWithLiterals =
  | EventStatus
  | 'SCHEDULED'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED';

export interface Event {
  /**
   * Event ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /** Event location. */
  location?: EventsLocation;
  /** Event scheduling. */
  scheduling?: Scheduling;
  /** Event title. */
  title?: string;
  /** Event description. */
  description?: string;
  /** Rich-text content that are displayed in a site's "About Event" section (HTML). */
  about?: string;
  /** Main event image. */
  mainImage?: string;
  /** Event slug URL (generated from event title). */
  slug?: string;
  /** ISO 639-1 language code of the event (used in content translations). */
  language?: string;
  /** Event creation timestamp. */
  created?: Date | null;
  /** Event modified timestamp. */
  modified?: Date | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /** RSVP or ticketing registration details. */
  registration?: Registration;
  /** "Add to calendar" URLs. */
  calendarLinks?: CalendarLinks;
  /** Event page URL components. */
  eventPageUrl?: SiteUrl;
  /** Event registration form. */
  form?: Form;
  /** Event dashboard summary of RSVP / ticket sales. */
  dashboard?: Dashboard;
  /** Instance ID of the site where event is hosted. */
  instanceId?: string;
  /** Guest list configuration. */
  guestListConfig?: GuestListConfig;
  /**
   * Event creator user ID.
   * @maxLength 36
   */
  userId?: string;
  /** Event discussion feed. For internal use. */
  feed?: Feed;
  /** Online conferencing details. */
  onlineConferencing?: OnlineConferencing;
  /** SEO settings. */
  seoSettings?: SeoSettings;
  /** Assigned contacts label key. */
  assignedContactsLabel?: string | null;
  /** Agenda details. */
  agenda?: Agenda;
  /** Categories this event is assigned to. */
  categories?: Category[];
  /** Visual settings for event. */
  eventDisplaySettings?: EventDisplaySettings;
  /** Rich content that are displayed in a site's "About Event" section. Successor to `about` field. */
  longDescription?: RichContent;
  /**
   * Event publish timestamp.
   * @readonly
   */
  publishedDate?: Date | null;
}

export interface Scheduling {
  /** Schedule configuration. */
  config?: ScheduleConfig;
  /** Formatted schedule representation. */
  formatted?: string;
  /** Formatted start date of the event (empty for TBD schedules). */
  startDateFormatted?: string;
  /** Formatted start time of the event (empty for TBD schedules). */
  startTimeFormatted?: string;
  /** Formatted end date of the event (empty for TBD schedules or when end date is hidden). */
  endDateFormatted?: string;
  /** Formatted end time of the event (empty for TBD schedules or when end date is hidden). */
  endTimeFormatted?: string;
}

export interface Registration {
  /** Event type. */
  type?: EventTypeWithLiterals;
  /** Event registration status. */
  status?: RegistrationStatusWithLiterals;
  /** RSVP collection details. */
  rsvpCollection?: RsvpCollection;
  /** Ticketing details. */
  ticketing?: Ticketing;
  /** External registration details. */
  external?: ExternalEvent;
  /** Types of users allowed to register. */
  restrictedTo?: VisitorTypeWithLiterals;
  /** Initial event type which was set when creating an event. */
  initialType?: EventTypeWithLiterals;
}

export enum EventType {
  /** Type not available for this request fieldset */
  NA_EVENT_TYPE = 'NA_EVENT_TYPE',
  /** Registration via RSVP */
  RSVP = 'RSVP',
  /** Registration via ticket purchase */
  TICKETS = 'TICKETS',
  /** External registration */
  EXTERNAL = 'EXTERNAL',
  /** Registration not available */
  NO_REGISTRATION = 'NO_REGISTRATION',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'NA_EVENT_TYPE'
  | 'RSVP'
  | 'TICKETS'
  | 'EXTERNAL'
  | 'NO_REGISTRATION';

export enum RegistrationStatus {
  /** Registration status is not applicable */
  NA_REGISTRATION_STATUS = 'NA_REGISTRATION_STATUS',
  /** Registration to event is closed */
  CLOSED = 'CLOSED',
  /** Registration to event is closed manually */
  CLOSED_MANUALLY = 'CLOSED_MANUALLY',
  /** Registration is open via RSVP */
  OPEN_RSVP = 'OPEN_RSVP',
  /** Registration to event waitlist is open via RSVP */
  OPEN_RSVP_WAITLIST = 'OPEN_RSVP_WAITLIST',
  /** Registration is open via ticket purchase */
  OPEN_TICKETS = 'OPEN_TICKETS',
  /** Registration is open via external URL */
  OPEN_EXTERNAL = 'OPEN_EXTERNAL',
  /** Registration will be open via RSVP */
  SCHEDULED_RSVP = 'SCHEDULED_RSVP',
}

/** @enumType */
export type RegistrationStatusWithLiterals =
  | RegistrationStatus
  | 'NA_REGISTRATION_STATUS'
  | 'CLOSED'
  | 'CLOSED_MANUALLY'
  | 'OPEN_RSVP'
  | 'OPEN_RSVP_WAITLIST'
  | 'OPEN_TICKETS'
  | 'OPEN_EXTERNAL'
  | 'SCHEDULED_RSVP';

export interface RsvpCollection {
  /** RSVP collection configuration. */
  config?: RsvpCollectionConfig;
}

export interface RsvpCollectionConfig {
  /** Defines the supported RSVP statuses. */
  rsvpStatusOptions?: RsvpStatusOptionsWithLiterals;
  /**
   * Total guest limit available to register to the event.
   * Additional guests per RSVP are counted towards total guests.
   */
  limit?: number | null;
  /** Whether a waitlist is opened when total guest limit is reached, allowing guests to create RSVP with WAITING RSVP status. */
  waitlist?: boolean;
  /** Registration start timestamp. */
  startDate?: Date | null;
  /** Registration end timestamp. */
  endDate?: Date | null;
}

export enum RsvpStatusOptions {
  /** Only YES RSVP status is available for RSVP registration */
  YES_ONLY = 'YES_ONLY',
  /** YES and NO RSVP status options are available for the registration */
  YES_AND_NO = 'YES_AND_NO',
}

/** @enumType */
export type RsvpStatusOptionsWithLiterals =
  | RsvpStatusOptions
  | 'YES_ONLY'
  | 'YES_AND_NO';

export interface RsvpConfirmationMessages {
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveConfirmation?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: RsvpConfirmationMessagesNegativeResponseConfirmation;
}

/** Confirmation shown after then registration when RSVP response is positive. */
export interface RsvpConfirmationMessagesPositiveResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

/** Confirmation shown after then registration when RSVP response is negative. */
export interface RsvpConfirmationMessagesNegativeResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export interface Ticketing {
  /**
   * Deprecated.
   * @deprecated
   */
  lowestPrice?: string | null;
  /**
   * Deprecated.
   * @deprecated
   */
  highestPrice?: string | null;
  /** Currency used in event transactions. */
  currency?: string | null;
  /** Ticketing configuration. */
  config?: TicketingConfig;
  /**
   * Price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPrice?: Money;
  /**
   * Price of highest priced ticket.
   * @readonly
   */
  highestTicketPrice?: Money;
  /**
   * Formatted price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPriceFormatted?: string | null;
  /**
   * Formatted price of highest priced ticket.
   * @readonly
   */
  highestTicketPriceFormatted?: string | null;
  /**
   * Whether all tickets are sold for this event.
   * @readonly
   */
  soldOut?: boolean | null;
}

export interface TicketingConfig {
  /** Whether the form must be filled out separately for each ticket. */
  guestAssignedTickets?: boolean;
  /** Tax configuration. */
  taxConfig?: TaxConfig;
  /**
   * Limit of tickets that can be purchased per order, default 20.
   * @max 50
   */
  ticketLimitPerOrder?: number;
  /**
   * Duration for which the tickets being bought are reserved.
   * @min 5
   * @max 30
   */
  reservationDurationInMinutes?: number | null;
}

export interface TaxConfig {
  /** Tax application settings. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @minLength 1
   * @maxLength 10
   */
  name?: string | null;
  /**
   * Tax rate (e.g.,`21.55`).
   * @decimalValue options { gte:0.001, lte:100, maxScale:3 }
   */
  rate?: string | null;
  /** Applies taxes for donations, default true. */
  appliesToDonations?: boolean | null;
}

export enum TaxType {
  /** Tax is included in the ticket price. */
  INCLUDED = 'INCLUDED',
  /** Tax is added to the order at the checkout. */
  ADDED = 'ADDED',
  /** Tax is added to the final total at the checkout. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED'
  | 'ADDED'
  | 'ADDED_AT_CHECKOUT';

export interface TicketsConfirmationMessages {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Download tickets" call-to-action label text.
   * @maxLength 50
   */
  downloadTicketsLabel?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export enum CheckoutType {
  /** Checkout using Events App. */
  EVENTS_APP = 'EVENTS_APP',
  /** Checkout using Ecomm Platform. */
  ECOMM_PLATFORM = 'ECOMM_PLATFORM',
}

/** @enumType */
export type CheckoutTypeWithLiterals =
  | CheckoutType
  | 'EVENTS_APP'
  | 'ECOMM_PLATFORM';

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface ExternalEvent {
  /** External event registration URL. */
  registration?: string;
}

export enum VisitorType {
  /** Site visitor (including member) */
  VISITOR = 'VISITOR',
  /** Site member */
  MEMBER = 'MEMBER',
  /** Site visitor or member */
  VISITOR_OR_MEMBER = 'VISITOR_OR_MEMBER',
}

/** @enumType */
export type VisitorTypeWithLiterals =
  | VisitorType
  | 'VISITOR'
  | 'MEMBER'
  | 'VISITOR_OR_MEMBER';

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

/** Site URL components */
export interface SiteUrl {
  /**
   * Base URL. For premium sites, this will be the domain.
   * For free sites, this would be site URL (e.g `mysite.wixsite.com/mysite`)
   */
  base?: string;
  /** The path to that page - e.g `/my-events/weekly-meetup-2` */
  path?: string;
}

/**
 * The form defines which elements are displayed to a site visitor during the registration process (RSVP or checkout).
 * It also contains customizable messages and labels.
 *
 *
 * A form is an ordered list of controls (blocks), which accept guest information into a field input.
 *
 * Each control contains one or more nested inputs. For example, `Name` control has two inputs:
 * - First Name
 * - Last Name
 *
 * By default, name and email controls are always required and are pinned to the top of the form.
 */
export interface Form {
  /** Nested fields as an ordered list. */
  controls?: InputControl[];
  /**
   * Set of defined form messages displayed in the UI before, during, and after a registration flow.
   * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
   */
  messages?: FormMessages;
}

/**
 * A block of nested fields.
 * Used to aggregate similar inputs like First Name and Last Name.
 */
export interface InputControl {
  /** Field control type. */
  type?: InputControlTypeWithLiterals;
  /** Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed. */
  system?: boolean;
  /**
   * Deprecated: Use `id` or `_id`.
   * @deprecated
   */
  name?: string;
  /** Child inputs. */
  inputs?: Input[];
  /**
   * *Deprecated:** Use `controls.inputs.label`.
   * @deprecated
   */
  label?: string;
  /** Field controls are sorted by this value in ascending order. */
  orderIndex?: number;
  /** Unique control ID. */
  _id?: string;
  /**
   * Whether the input control is deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export enum InputControlType {
  /** Single text value field. */
  INPUT = 'INPUT',
  /** Single text value field with multiple lines. */
  TEXTAREA = 'TEXTAREA',
  /** Single-choice field with predefined values. */
  DROPDOWN = 'DROPDOWN',
  /** Single-choice field with predefined values. */
  RADIO = 'RADIO',
  /** Multiple-choice field with predefined values. */
  CHECKBOX = 'CHECKBOX',
  /** Fields for entering first and last names. */
  NAME = 'NAME',
  /** Fields for additional guests and their respective names. */
  GUEST_CONTROL = 'GUEST_CONTROL',
  /** Single-line address field. */
  ADDRESS_SHORT = 'ADDRESS_SHORT',
  /** Full address field with multiple lines. */
  ADDRESS_FULL = 'ADDRESS_FULL',
  /** Fields for entering year, month, and day. */
  DATE = 'DATE',
}

/** @enumType */
export type InputControlTypeWithLiterals =
  | InputControlType
  | 'INPUT'
  | 'TEXTAREA'
  | 'DROPDOWN'
  | 'RADIO'
  | 'CHECKBOX'
  | 'NAME'
  | 'GUEST_CONTROL'
  | 'ADDRESS_SHORT'
  | 'ADDRESS_FULL'
  | 'DATE';

/** Child inputs. */
export interface Input {
  /** Field name. */
  name?: string;
  /**
   * *Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.
   * @deprecated
   */
  array?: boolean;
  /** Main field label. */
  label?: string;
  /** Additional labels for multi-valued fields such as address. */
  additionalLabels?: Record<string, string>;
  /** Predefined choice options for fields, such as dropdown. */
  options?: string[];
  /** Whether field is mandatory. */
  mandatory?: boolean;
  /** Maximum number of accepted characters (relevant for text fields). */
  maxLength?: number;
  /**
   * Type which determines field format.
   * Used to validate submitted response.
   */
  type?: ValueTypeWithLiterals;
  /**
   * The maximum number of accepted values for array input.
   *
   * **Note:** Only applicable for `TEXT_ARRAY` input fields.
   */
  maxSize?: number | null;
  /**
   * Default option initially selected when an input has multiple choices.
   *
   * Defaults to first (0th) option, if not configured.
   * Currently only applicable for `type.dropdown`.
   */
  defaultOptionSelection?: OptionSelection;
  /**
   * Additional labels for multi-valued fields, such as address.
   * @readonly
   */
  labels?: Label[];
}

export enum ValueType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  TEXT_ARRAY = 'TEXT_ARRAY',
  DATE_TIME = 'DATE_TIME',
  ADDRESS = 'ADDRESS',
}

/** @enumType */
export type ValueTypeWithLiterals =
  | ValueType
  | 'TEXT'
  | 'NUMBER'
  | 'TEXT_ARRAY'
  | 'DATE_TIME'
  | 'ADDRESS';

/**
 * Describes initially selected option when an input has multiple choices.
 * Defaults to first (0th) option if not configured.
 */
export interface OptionSelection extends OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

/** @oneof */
export interface OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

export interface Label {
  /** Field name. */
  name?: string;
  /** Field label. */
  label?: string;
}

/**
 * Defines form messages shown in UI before, during, and after registration flow.
 * It enables configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
 */
export interface FormMessages {
  /** [RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages. */
  rsvp?: RsvpFormMessages;
  /** Checkout form messages. */
  checkout?: CheckoutFormMessages;
  /** Messages shown when event registration is closed. */
  registrationClosed?: RegistrationClosedMessages;
  /** Messages shown when event tickets are unavailable. */
  ticketsUnavailable?: TicketsUnavailableMessages;
}

export interface RsvpFormMessages {
  /** Label text indicating RSVP's `status` is `"YES"`. */
  rsvpYesOption?: string;
  /** Label text indicating RSVP's `status` is `"NO"`. */
  rsvpNoOption?: string;
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: Negative;
  /** "Submit form" call-to-action label text. */
  submitActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface PositiveResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarActionLabel?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface NegativeResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Set of messages shown during registration when RSVP response is positive. */
export interface Positive {
  /** Main form title for positive response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: PositiveResponseConfirmation;
}

/** A set of messages shown during registration with negative response */
export interface Negative {
  /** Main form title for negative response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: NegativeResponseConfirmation;
}

export interface CheckoutFormMessages {
  /** Main form title for response. */
  title?: string;
  /** Submit form call-to-action label text. */
  submitActionLabel?: string;
  /** Confirmation messages shown after checkout. */
  confirmation?: ResponseConfirmation;
}

/** Confirmation messages shown after checkout. */
export interface ResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Download tickets" call-to-action label text. */
  downloadTicketsLabel?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarLabel?: string;
  /** "Share event" call-to-action label text. */
  shareEventLabel?: string;
}

export interface RegistrationClosedMessages {
  /** Message shown when event registration is closed. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface TicketsUnavailableMessages {
  /** Message shown when event tickets are unavailable. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface Dashboard {
  /** Guest RSVP summary. */
  rsvpSummary?: RsvpSummary;
  /**
   * Summary of revenue and tickets sold.
   * (Archived orders are not included).
   */
  ticketingSummary?: TicketingSummary;
}

export interface RsvpSummary {
  /** Total number of RSVPs. */
  total?: number;
  /** Number of RSVPs with status `YES`. */
  yes?: number;
  /** Number of RSVPs with status `NO`. */
  no?: number;
  /** Number of RSVPs in waitlist. */
  waitlist?: number;
}

export interface TicketingSummary {
  /** Number of tickets sold. */
  tickets?: number;
  /**
   * Total revenue, excluding fees.
   * (taxes and payment provider fees are not deducted.)
   */
  revenue?: Money;
  /** Whether currency is locked and cannot be changed (generally occurs after the first order in the specified currency has been created). */
  currencyLocked?: boolean;
  /** Number of orders placed. */
  orders?: number;
  /** Total balance of confirmed transactions. */
  totalSales?: Money;
}

export interface GuestListConfig {
  /** Whether members can see other members attending the event (defaults to true). */
  publicGuestList?: boolean;
}

export interface Feed {
  /** Event discussion feed token. */
  token?: string;
}

export interface OnlineConferencing {
  config?: OnlineConferencingConfig;
  session?: OnlineConferencingSession;
}

export interface OnlineConferencingConfig {
  /**
   * Whether online conferencing is enabled (not supported for TBD schedules).
   * When enabled, links to join conferencing are generated and provided to guests.
   */
  enabled?: boolean;
  /**
   * Conferencing provider ID.
   * @format GUID
   */
  providerId?: string | null;
  /** Conference type */
  conferenceType?: ConferenceTypeWithLiterals;
}

export enum ConferenceType {
  /** Everyone in the meeting can publish and subscribe video and audio. */
  MEETING = 'MEETING',
  /** Guests can only subscribe to video and audio. */
  WEBINAR = 'WEBINAR',
}

/** @enumType */
export type ConferenceTypeWithLiterals = ConferenceType | 'MEETING' | 'WEBINAR';

export interface OnlineConferencingSession {
  /**
   * Link for event host to start the online conference session.
   * @readonly
   */
  hostLink?: string;
  /**
   * Link for guests to join the online conference session.
   * @readonly
   */
  guestLink?: string;
  /**
   * The password required to join online conferencing session (when relevant).
   * @readonly
   */
  password?: string | null;
  /**
   * Indicates that session was created successfully on providers side.
   * @readonly
   */
  sessionCreated?: boolean | null;
  /**
   * Unique session id
   * @readonly
   */
  sessionId?: string | null;
}

export interface SeoSettings {
  /**
   * URL slug
   * @maxLength 130
   */
  slug?: string;
  /** Advanced SEO data */
  advancedSeoData?: SeoSchema;
  /**
   * Hidden from SEO Site Map
   * @readonly
   */
  hidden?: boolean | null;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface Agenda {
  /** Whether the schedule is enabled for the event. */
  enabled?: boolean;
  /**
   * Agenda page URL.
   * @readonly
   */
  pageUrl?: SiteUrl;
}

/**
 * A Category is a classification object that groups related events on a site so you can organize and display them by themes, venues, or other facets.
 *
 * You can manage Categories, assign events to them, and use them to control the selection and order of events across different pages and widgets.
 *
 * Read more about [Categories](https://support.wix.com/en/article/creating-and-displaying-event-categories).
 */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Category name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string;
  /**
   * Date and time when category was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * The total number of draft and published events assigned to the category.
   * @readonly
   */
  counts?: CategoryCounts;
  /**
   * Category state.
   *
   * Default: `MANUAL`.
   *
   * **Note:** The WIX_EVENTS.MANAGE_AUTO_CATEGORIES permission scope is required to use states other than `MANUAL`.
   * @maxSize 3
   */
  states?: CategoryStateStateWithLiterals[];
}

export interface CategoryCounts {
  /** Total number of draft events assigned to the category. */
  assignedEventsCount?: number | null;
  /** Total number of published events assigned to the category. Deleted events are excluded. */
  assignedDraftEventsCount?: number | null;
}

export enum CategoryStateState {
  /** Categoty is created manually by the user. */
  MANUAL = 'MANUAL',
  /** Category is created automatically. */
  AUTO = 'AUTO',
  /** Category is created automatically when publishing recurring events. */
  RECURRING_EVENT = 'RECURRING_EVENT',
  /** Category is hidden. */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type CategoryStateStateWithLiterals =
  | CategoryStateState
  | 'MANUAL'
  | 'AUTO'
  | 'RECURRING_EVENT'
  | 'HIDDEN';

export interface EventDisplaySettings {
  /** Whether event details button is hidden. Only available for events with no registration. */
  hideEventDetailsButton?: boolean | null;
  /** Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page. */
  hideEventDetailsPage?: boolean | null;
}

export interface LabellingSettings {}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
}

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface Badge {
  /** Badge type. */
  type?: TypeWithLiterals;
  /**
   * Badge text.
   * @maxLength 50
   */
  text?: string | null;
}

export enum Type {
  /** 1st priority badge type. */
  FIRST_PRIORITY = 'FIRST_PRIORITY',
  /** 2nd priority badge type. */
  SECOND_PRIORITY = 'SECOND_PRIORITY',
  /** 3rd priority badge type. */
  THIRD_PRIORITY = 'THIRD_PRIORITY',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'FIRST_PRIORITY'
  | 'SECOND_PRIORITY'
  | 'THIRD_PRIORITY';

export interface EventUpdated {
  /** Event update timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: EventsLocation;
  /** Event schedule configuration. */
  scheduleConfig?: ScheduleConfig;
  /** Event title. */
  title?: string;
  /**
   * Whether schedule configuration was updated.
   * @deprecated
   */
  scheduleConfigUpdated?: boolean;
  /** Updated event */
  event?: Event;
}

/** Triggers when seating plan category summaries are updated. */
export interface SeatingPlanCategoriesSummaryUpdated {
  /**
   * Seating plan ID.
   * @format GUID
   */
  seatingPlanId?: string;
  /** External seating plan ID. */
  externalSeatingPlanId?: string | null;
  /**
   * Updated category capacity and reservation counts.
   * @maxSize 100
   */
  categories?: CategoryDetails[];
  /**
   * Summary revision number for cache invalidation.
   * @readonly
   */
  revision?: string | null;
}

/** Category capacity and reservation summary. */
export interface CategoryDetails {
  /**
   * Seating Plan ID.
   * @format GUID
   * @readonly
   */
  seatingPlanId?: string | null;
  /**
   * External Seating Plan ID used for integration with third-party systems.
   * @minLength 1
   * @maxLength 100
   * @readonly
   */
  externalSeatingPlanId?: string | null;
  /**
   * External Category ID used for mapping to venue-specific category names. For example, `VIP` or `ORCHESTRA`.
   * @minLength 1
   * @maxLength 100
   * @readonly
   */
  externalCategoryId?: string | null;
  /**
   * Total seating capacity available in category.
   * @readonly
   */
  totalCapacity?: number | null;
  /**
   * Number of seats currently reserved in category.
   * @readonly
   */
  reserved?: number | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface GetTicketDefinitionFromTrashBinRequest {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /**
   * Requested fields. Not implemented.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

export interface GetTicketDefinitionFromTrashBinResponse {
  /** The requested ticket definition. */
  ticketDefinition?: TicketDefinition;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface GetTicketDefinitionSummaryRequest {
  /**
   * Ticket definition id.
   * @format GUID
   */
  ticketDefinitionId?: string | null;
}

export interface GetTicketDefinitionSummaryResponse {
  /** Summary. */
  summary?: TicketDefinitionSummary;
}

export interface TicketDefinitionSummary {
  /**
   * Ticket definition ID.
   * @format GUID
   * @readonly
   */
  definitionId?: string | null;
  /**
   * Event ID to which the ticket definition summary belongs.
   * @format GUID
   * @readonly
   */
  eventId?: string | null;
  /**
   * Date and time of the ticket definition summary latest update in `yyyy-mm-ddThh:mm:sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Reserved count.
   * @readonly
   */
  reservedCount?: number;
  /**
   * Sold count.
   * @readonly
   */
  soldCount?: number;
  /**
   * Paid exists.
   * @readonly
   */
  paidExists?: boolean;
}

export interface ListEventTicketingSummaryRequest {
  /**
   * Event ID.
   * @format GUID
   * @minSize 1
   * @maxSize 1000
   */
  eventId?: string[];
}

export interface ListEventTicketingSummaryResponse {
  /** Ticketing summaries. */
  summaries?: EventTicketingSummary[];
}

export interface EventTicketingSummary {
  /**
   * Event ID to which the ticketing summary belongs.
   * @format GUID
   * @readonly
   */
  eventId?: string | null;
  /**
   * Date and time of the ticketing summary latest update in `yyyy-mm-ddThh:mm:sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Whether all tickets are sold out for this event.
   * @readonly
   */
  soldOut?: boolean | null;
  /**
   * Price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPrice?: CommonMoney;
  /**
   * Price of highest priced ticket.
   * @readonly
   */
  highestTicketPrice?: CommonMoney;
  /**
   * Currency used in event transactions.
   * @minLength 3
   * @maxLength 3
   * @readonly
   */
  currency?: string | null;
  /**
   * Formatted price of lowest priced ticket.
   * @maxLength 50
   * @readonly
   */
  lowestTicketPriceFormatted?: string | null;
  /**
   * Formatted price of highest priced ticket.
   * @maxLength 50
   * @readonly
   */
  highestTicketPriceFormatted?: string | null;
}

export interface UpdateEventTicketingSummaryRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
}

export interface UpdateEventTicketingSummaryResponse {}

/** @docsIgnore */
export type CreateTicketDefinitionApplicationErrors =
  | {
      code?: 'INVALID_EVENT_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TICKET_DEFINITIONS_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_SALE_PERIOD';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateTicketDefinitionApplicationErrors = {
  code?: 'INVALID_SALE_PERIOD';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ReorderTicketDefinitionsApplicationErrors = {
  code?: 'INVALID_REORDER_INSTRUCTION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CountTicketDefinitionsApplicationErrors = {
  code?: 'FILTER_PARSER_ERROR';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CountAvailableTicketDefinitionsApplicationErrors = {
  code?: 'FILTER_PARSER_ERROR';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ChangeCurrencyApplicationErrors = {
  code?: 'CURRENCY_LOCKED';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface TicketDefinitionCreatedEnvelope {
  entity: TicketDefinition;
  metadata: EventMetadata;
}

/**
 * Triggered when a ticket definition is created.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_created
 * @slug created
 */
export declare function onTicketDefinitionCreated(
  handler: (event: TicketDefinitionCreatedEnvelope) => void | Promise<void>
): void;

export interface TicketDefinitionDeletedEnvelope {
  entity: TicketDefinition;
  metadata: EventMetadata;
}

/**
 * Triggered when a ticket definition is deleted.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_deleted
 * @slug deleted
 */
export declare function onTicketDefinitionDeleted(
  handler: (event: TicketDefinitionDeletedEnvelope) => void | Promise<void>
): void;

export interface TicketDefinitionSaleEndedEnvelope {
  data: TicketDefinitionSaleEnded;
  metadata: EventMetadata;
}

/**
 * Triggered when a ticket sale ends. Sales details aren't included in the response.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_sale_ended
 * @slug sale_ended
 */
export declare function onTicketDefinitionSaleEnded(
  handler: (event: TicketDefinitionSaleEndedEnvelope) => void | Promise<void>
): void;

export interface TicketDefinitionSalePeriodUpdatedEnvelope {
  data: SalePeriodUpdated;
  metadata: EventMetadata;
}

/**
 * Triggered when a sale period is updated.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_sale_period_updated
 * @slug sale_period_updated
 */
export declare function onTicketDefinitionSalePeriodUpdated(
  handler: (
    event: TicketDefinitionSalePeriodUpdatedEnvelope
  ) => void | Promise<void>
): void;

export interface TicketDefinitionSaleStartedEnvelope {
  data: TicketDefinitionSaleStarted;
  metadata: EventMetadata;
}

/**
 * Triggered when a ticket sale starts. Sales details aren't included in the response.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_sale_started
 * @slug sale_started
 */
export declare function onTicketDefinitionSaleStarted(
  handler: (event: TicketDefinitionSaleStartedEnvelope) => void | Promise<void>
): void;

export interface TicketDefinitionUpdatedEnvelope {
  entity: TicketDefinition;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a ticket definition is updated.
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-ORDERS
 * @permissionScope Manage Ticket Definitions
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-TICKET-DEF
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @webhook
 * @eventType wix.events.v3.ticket_definition_updated
 * @slug updated
 */
export declare function onTicketDefinitionUpdated(
  handler: (event: TicketDefinitionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a ticket definition.
 *
 * You may create up to 100 definitions per event.
 * @param ticketDefinition - Ticket definition info.
 * @public
 * @requiredField ticketDefinition
 * @requiredField ticketDefinition.eventId
 * @requiredField ticketDefinition.feeType
 * @requiredField ticketDefinition.name
 * @requiredField ticketDefinition.pricingMethod
 * @param options - Currency info.
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @returns Created ticket definition.
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.CreateTicketDefinition
 */
export async function createTicketDefinition(
  ticketDefinition: NonNullablePaths<
    TicketDefinition,
    `eventId` | `feeType` | `name` | `pricingMethod`,
    2
  >,
  options?: CreateTicketDefinitionOptions
): Promise<
  NonNullablePaths<
    TicketDefinition,
    | `hidden`
    | `limited`
    | `pricingMethod.fixedPrice.value`
    | `pricingMethod.fixedPrice.currency`
    | `pricingMethod.pricingOptions.optionDetails`
    | `pricingMethod.pricingType`
    | `pricingMethod.free`
    | `feeType`
    | `salePeriod.displayNotOnSale`
    | `saleStatus`
    | `eventDetails.location.type`
    | `eventDetails.location.address.streetAddress.number`
    | `eventDetails.location.address.streetAddress.name`
    | `eventDetails.dateAndTimeSettings.recurrenceStatus`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
    | `eventDetails.status`
    | `seatingDetails.places`
    | `seatingDetails.places.${number}.placeId`
    | `seatingDetails.places.${number}.label`
    | `seatingDetails.places.${number}.availableCapacity`,
    7
  > & {
    __applicationErrorsType?: CreateTicketDefinitionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      ticketDefinition: ticketDefinition,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [{ path: 'ticketDefinition.eventDetails.eventPageUrl' }],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'ticketDefinition.eventDetails.location.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.createTicketDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'ticketDefinition.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ticketDefinition.eventDetails.location.address' }],
        },
      ])
    )?.ticketDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ticketDefinition: '$[0]',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['ticketDefinition', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateTicketDefinitionOptions {
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

/**
 * Updates a ticket definition.
 *
 * Each time the ticket definition is updated, `revision` increments by 1. The existing `revision` must be included when updating the ticket definition. This ensures you're working with the latest ticket definition and prevents unintended overwrites.
 * @param _id - Ticket definition ID.
 * @public
 * @requiredField _id
 * @requiredField ticketDefinition
 * @requiredField ticketDefinition.revision
 * @param options - Optional fields.
 * @param ticketDefinition - Ticket definition to update.
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @returns The updated ticket definition.
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.UpdateTicketDefinition
 */
export async function updateTicketDefinition(
  _id: string,
  ticketDefinition: NonNullablePaths<UpdateTicketDefinition, `revision`, 2>,
  options?: UpdateTicketDefinitionOptions
): Promise<
  NonNullablePaths<
    TicketDefinition,
    | `hidden`
    | `limited`
    | `pricingMethod.fixedPrice.value`
    | `pricingMethod.fixedPrice.currency`
    | `pricingMethod.pricingOptions.optionDetails`
    | `pricingMethod.pricingType`
    | `pricingMethod.free`
    | `feeType`
    | `salePeriod.displayNotOnSale`
    | `saleStatus`
    | `eventDetails.location.type`
    | `eventDetails.location.address.streetAddress.number`
    | `eventDetails.location.address.streetAddress.name`
    | `eventDetails.dateAndTimeSettings.recurrenceStatus`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
    | `eventDetails.status`
    | `seatingDetails.places`
    | `seatingDetails.places.${number}.placeId`
    | `seatingDetails.places.${number}.label`
    | `seatingDetails.places.${number}.availableCapacity`,
    7
  > & {
    __applicationErrorsType?: UpdateTicketDefinitionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      ticketDefinition: { ...ticketDefinition, id: _id },
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [{ path: 'ticketDefinition.eventDetails.eventPageUrl' }],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'ticketDefinition.eventDetails.location.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.updateTicketDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'ticketDefinition.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ticketDefinition.eventDetails.location.address' }],
        },
      ])
    )?.ticketDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { ticketDefinition: '$[1]' },
        explicitPathsToArguments: {
          'ticketDefinition.id': '$[0]',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'ticketDefinition', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateTicketDefinition {
  /**
   * Ticket definition ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Event ID to which the ticket definition belongs.
   * @format GUID
   * @immutable
   */
  eventId?: string | null;
  /**
   * Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ticket definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the ticket definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Ticket definition name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string | null;
  /**
   * Ticket definition description.
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Ticket definition policy.
   * @maxLength 1000
   */
  policyText?: string | null;
  /** Whether this ticket definition is hidden from site visitors and can't be purchased. */
  hidden?: boolean;
  /**
   * Whether the ticket has a limited maximum quantity.
   * @readonly
   */
  limited?: boolean;
  /** The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request. */
  initialLimit?: number | null;
  /**
   * The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.
   * @readonly
   */
  actualLimit?: number | null;
  /** Ticket pricing method. */
  pricingMethod?: PricingMethod;
  /** Type of ticket service fee to collect. */
  feeType?: FeeTypeEnumTypeWithLiterals;
  /** Ticket sale period. */
  salePeriod?: SalePeriod;
  /**
   * Ticket sale status.
   * @readonly
   */
  saleStatus?: SaleStatusEnumStatusWithLiterals;
  /**
   * Ticket sales information. <br> <br>
   * **Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.
   * @readonly
   */
  salesDetails?: SalesDetails;
  /**
   * Number of tickets that can be purchased per checkout. <br> <br>
   * **Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.
   * @readonly
   * @max 50
   */
  limitPerCheckout?: number | null;
  /** Data extensions. */
  extendedFields?: ExtendedFields;
  /**
   * Information about the even the ticket is for. <br>
   * **Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.
   * @readonly
   */
  eventDetails?: EventDetails;
  /**
   * Seating information including available seats and areas for this ticket definition. <br>
   * **Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).
   * @readonly
   */
  seatingDetails?: SeatingDetails;
}

export interface UpdateTicketDefinitionOptions {
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

/**
 * Retrieves a ticket definition.
 * @param ticketDefinitionId - Ticket definition ID.
 * @public
 * @requiredField ticketDefinitionId
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @returns The requested ticket definition.
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.GetTicketDefinition
 */
export async function getTicketDefinition(
  ticketDefinitionId: string,
  options?: GetTicketDefinitionOptions
): Promise<
  NonNullablePaths<
    TicketDefinition,
    | `hidden`
    | `limited`
    | `pricingMethod.fixedPrice.value`
    | `pricingMethod.fixedPrice.currency`
    | `pricingMethod.pricingOptions.optionDetails`
    | `pricingMethod.pricingType`
    | `pricingMethod.free`
    | `feeType`
    | `salePeriod.displayNotOnSale`
    | `saleStatus`
    | `eventDetails.location.type`
    | `eventDetails.location.address.streetAddress.number`
    | `eventDetails.location.address.streetAddress.name`
    | `eventDetails.dateAndTimeSettings.recurrenceStatus`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
    | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
    | `eventDetails.status`
    | `seatingDetails.places`
    | `seatingDetails.places.${number}.placeId`
    | `seatingDetails.places.${number}.label`
    | `seatingDetails.places.${number}.availableCapacity`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ticketDefinitionId: ticketDefinitionId,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.getTicketDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'ticketDefinition.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ticketDefinition.eventDetails.location.address' }],
        },
      ])
    )?.ticketDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ticketDefinitionId: '$[0]',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['ticketDefinitionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetTicketDefinitionOptions {
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

/**
 * Deletes a ticket definition.
 * @param ticketDefinitionId - ID of the ticket definition to delete.
 * @public
 * @requiredField ticketDefinitionId
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.DeleteTicketDefinition
 */
export async function deleteTicketDefinition(
  ticketDefinitionId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ticketDefinitionId: ticketDefinitionId,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.deleteTicketDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ticketDefinitionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ticketDefinitionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adjusts the order in which ticket definitions display in an event dashboard, as well as the order of available tickets displayed in the ticket picker.
 * > **Note:** You may use both `beforeTicketDefinitionId` and `afterTicketDefinitionId` but only the last one provided takes effect.
 * @param eventId - Event ID.
 * @public
 * @requiredField eventId
 * @requiredField options.ticketDefinitionId
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.ReorderTicketDefinitions
 */
export async function reorderTicketDefinitions(
  eventId: string,
  options?: NonNullablePaths<
    ReorderTicketDefinitionsOptions,
    `ticketDefinitionId`,
    2
  >
): Promise<
  void & {
    __applicationErrorsType?: ReorderTicketDefinitionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    ticketDefinitionId: options?.ticketDefinitionId,
    beforeDefinitionId: options?.beforeDefinitionId,
    afterDefinitionId: options?.afterDefinitionId,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.reorderTicketDefinitions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          ticketDefinitionId: '$[1].ticketDefinitionId',
          beforeDefinitionId: '$[1].beforeDefinitionId',
          afterDefinitionId: '$[1].afterDefinitionId',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ReorderTicketDefinitionsOptions
  extends ReorderTicketDefinitionsOptionsReferenceDefinitionOneOf {
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId: string;
  /**
   * Move the given `definitionId` before the referenced ticket definition.
   * @format GUID
   */
  beforeDefinitionId?: string;
  /**
   * Move the given `definitionId` after the referenced ticket definition.
   * @format GUID
   */
  afterDefinitionId?: string;
}

/** @oneof */
export interface ReorderTicketDefinitionsOptionsReferenceDefinitionOneOf {
  /**
   * Move the given `definitionId` before the referenced ticket definition.
   * @format GUID
   */
  beforeDefinitionId?: string;
  /**
   * Move the given `definitionId` after the referenced ticket definition.
   * @format GUID
   */
  afterDefinitionId?: string;
}

/**
 * Retrieves a list of ticket definitions, given the provided paging, filtering, and sorting.
 * Query Ticket Definitions runs with these defaults, which you can override:
 * - `createdDate` is sorted in `ASC` order
 * - `paging.limit` is `100`
 * - `paging.offset` is `0`
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.QueryTicketDefinitions
 */
export function queryTicketDefinitions(
  options?: QueryTicketDefinitionsOptions
): TicketDefinitionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    TicketDefinition,
    'CURSOR',
    QueryTicketDefinitionsRequest,
    QueryTicketDefinitionsResponse
  >({
    func: async (payload: QueryTicketDefinitionsRequest) => {
      const reqOpts =
        ambassadorWixEventsV3TicketDefinition.queryTicketDefinitions({
          ...payload,
          ...(options ?? {}),
        });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryTicketDefinitionsRequest['query']) => {
      const args = [query, options] as [
        QueryTicketDefinitionsRequest['query'],
        QueryTicketDefinitionsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryTicketDefinitionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTPageURLToSDKPageURL,
            paths: [{ path: 'ticketDefinitions.eventDetails.eventPageUrl' }],
          },
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'ticketDefinitions.eventDetails.location.address' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.ticketDefinitions,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryTicketDefinitionsOptions {
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[] | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface TicketDefinitionsQueryResult extends QueryCursorResult {
  items: TicketDefinition[];
  query: TicketDefinitionsQueryBuilder;
  next: () => Promise<TicketDefinitionsQueryResult>;
  prev: () => Promise<TicketDefinitionsQueryResult>;
}

export interface TicketDefinitionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus',
    value: any
  ) => TicketDefinitionsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus',
    value: boolean
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus'
    >
  ) => TicketDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'eventId'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'hidden'
      | 'initialLimit'
      | 'pricingMethod.fixedPrice.value'
      | 'pricingMethod.fixedPrice.currency'
      | 'pricingMethod.pricingType'
      | 'pricingMethod.free'
      | 'feeType'
      | 'saleStatus'
    >
  ) => TicketDefinitionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => TicketDefinitionsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => TicketDefinitionsQueryBuilder;
  find: () => Promise<TicketDefinitionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.QueryTicketDefinitions
 * @requiredField query
 */
export async function typedQueryTicketDefinitions(
  query: TicketDefinitionQuery,
  options?: QueryTicketDefinitionsOptions
): Promise<
  NonNullablePaths<
    QueryTicketDefinitionsResponse,
    | `ticketDefinitions`
    | `ticketDefinitions.${number}.hidden`
    | `ticketDefinitions.${number}.limited`
    | `ticketDefinitions.${number}.pricingMethod.fixedPrice.value`
    | `ticketDefinitions.${number}.pricingMethod.fixedPrice.currency`
    | `ticketDefinitions.${number}.pricingMethod.pricingType`
    | `ticketDefinitions.${number}.pricingMethod.free`
    | `ticketDefinitions.${number}.feeType`
    | `ticketDefinitions.${number}.salePeriod.displayNotOnSale`
    | `ticketDefinitions.${number}.saleStatus`
    | `ticketDefinitions.${number}.eventDetails.location.type`
    | `ticketDefinitions.${number}.eventDetails.dateAndTimeSettings.recurrenceStatus`
    | `ticketDefinitions.${number}.eventDetails.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.queryTicketDefinitions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'ticketDefinitions.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ticketDefinitions.eventDetails.location.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TicketDefinitionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'description',
        'eventId',
        'initialLimit',
        'name',
        'pricingMethod.fixedPrice.currency',
        'pricingMethod.fixedPrice.value',
        'revision'
      ];
      operators: [
        '$eq',
        '$exists',
        '$gt',
        '$gte',
        '$in',
        '$lt',
        '$lte',
        '$ne',
        '$nin'
      ];
      sort: 'BOTH';
    },
    {
      fields: [
        'feeType',
        'hidden',
        'pricingMethod.free',
        'pricingMethod.pricingType',
        'saleStatus'
      ];
      operators: ['$eq', '$exists', '$in', '$ne', '$nin'];
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  TicketDefinition,
  TicketDefinitionQuerySpec
>;
export type TicketDefinitionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort` parameters.  
  */
  cursorPaging?: {
    /** 
  Number of items to load per page. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.
  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1", "fieldName2":{"$operator":"value2"} }`. <br/> <br/> **Example:** <br/> `"filter" : { "id": "2224a9d1-79e6-4549-a5c5-bf7ce5aac1a5", "revision": {"$ne":"1"} }`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format: <br/> `[{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]` <br/> <br/> **Example:** <br/> `[{"fieldName":"createdDate","direction":"DESC"}]` <br/> <br/> See [supported fields](https://dev.wix.com/api/rest/wix-events/policy-v2/filter-and-sort) for more information. 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 100 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Defaults to `ASC`  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      TicketDefinition,
      TicketDefinitionQuerySpec,
      TicketDefinitionQuery
    >(),
  },
};

/**
 * Retrieves a list of available ticket definitions with the specified paging, filtering, and sorting.
 *
 * This method retrieves ticket definitions that aren't in the `hidden` state. The available ticket definitions can be retrieved by site visitors.
 *
 * > **Note:** You need the `WIX_EVENTS.READ_TICKET_DEFINITIONS` permission to get the `salesDetails` field values.
 *
 * Query Available Ticket Definitions runs with these defaults, which you can override:
 * - `createdDate` is sorted in `ASC` order
 * - `paging.limit` is `100`
 * - `paging.offset` is `0`
 *
 * To learn about working with _Query_ methods, see [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language).
 * @param query - Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.
 * @public
 * @requiredField query
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_AVAILABLE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.QueryAvailableTicketDefinitions
 */
export async function queryAvailableTicketDefinitions(
  query: QueryV2,
  options?: QueryAvailableTicketDefinitionsOptions
): Promise<
  NonNullablePaths<
    QueryAvailableTicketDefinitionsResponse,
    | `ticketDefinitions`
    | `ticketDefinitions.${number}.hidden`
    | `ticketDefinitions.${number}.limited`
    | `ticketDefinitions.${number}.pricingMethod.fixedPrice.value`
    | `ticketDefinitions.${number}.pricingMethod.fixedPrice.currency`
    | `ticketDefinitions.${number}.pricingMethod.pricingType`
    | `ticketDefinitions.${number}.pricingMethod.free`
    | `ticketDefinitions.${number}.feeType`
    | `ticketDefinitions.${number}.salePeriod.displayNotOnSale`
    | `ticketDefinitions.${number}.saleStatus`
    | `ticketDefinitions.${number}.eventDetails.location.type`
    | `ticketDefinitions.${number}.eventDetails.dateAndTimeSettings.recurrenceStatus`
    | `ticketDefinitions.${number}.eventDetails.status`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.queryAvailableTicketDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'ticketDefinitions.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ticketDefinitions.eventDetails.location.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]', fields: '$[1].fields' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryAvailableTicketDefinitionsOptions {
  /**
   * Predefined sets of fields to return.
   * @maxSize 5
   */
  fields?: FieldWithLiterals[];
}

/**
 * Counts ticket definitions by the `saleStatus` field, including those with the `hidden` status.
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.CountTicketDefinitions
 */
export async function countTicketDefinitions(
  options?: CountTicketDefinitionsOptions
): Promise<
  CountTicketDefinitionsResponse & {
    __applicationErrorsType?: CountTicketDefinitionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
    facet: options?.facet,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.countTicketDefinitions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0].filter',
          facet: '$[0].facet',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountTicketDefinitionsOptions {
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`. */
  filter?: Record<string, any> | null;
  /**
   * Parameters to count ticket definitions by.
   *
   * - Max: 20 facets.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
}

/**
 * Counts ticket definitions by the `saleStatus`, excluding those with the `hidden` status.
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_AVAILABLE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.CountAvailableTicketDefinitions
 */
export async function countAvailableTicketDefinitions(
  options?: CountAvailableTicketDefinitionsOptions
): Promise<
  CountAvailableTicketDefinitionsResponse & {
    __applicationErrorsType?: CountAvailableTicketDefinitionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.countAvailableTicketDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountAvailableTicketDefinitionsOptions {
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`. */
  filter?: Record<string, any> | null;
}

/**
 * Deletes multiple ticket definitions.
 *
 * All ticket definitions that meet the specified `filter` criteria are deleted.
 * @param filter - Filter object. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.
 * @public
 * @requiredField filter
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.BulkDeleteTicketDefinitionsByFilter
 */
export async function bulkDeleteTicketDefinitionsByFilter(
  filter: Record<string, any>
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ filter: filter });

  const reqOpts =
    ambassadorWixEventsV3TicketDefinition.bulkDeleteTicketDefinitionsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['filter']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Changes ticket price currency per event.
 *
 * Updates the currency for all ticket definitions under the event.
 * This doesn't convert price values. It only updates the currency code
 * (for example, 10 USD becomes 10 EUR).
 * @param eventId - Event ID.
 * @public
 * @requiredField eventId
 * @requiredField options
 * @requiredField options.currency
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_TICKET_DEFINITIONS
 * @applicableIdentity APP
 * @fqn wix.events.ticketdef.v3.TicketDefinitionManagement.ChangeCurrency
 */
export async function changeCurrency(
  eventId: string,
  options: NonNullablePaths<ChangeCurrencyOptions, `currency`, 2>
): Promise<
  void & {
    __applicationErrorsType?: ChangeCurrencyApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    currency: options?.currency,
  });

  const reqOpts = ambassadorWixEventsV3TicketDefinition.changeCurrency(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          currency: '$[1].currency',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ChangeCurrencyOptions {
  /**
   * Ticket price currency in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @minLength 3
   * @maxLength 3
   */
  currency: string;
}
